// Időkapu-foglaló · iter146 — Slot-jegy (QR ticket) component.
//
// Renders a self-contained "slot ticket" card:
//   - Plant name, dock/gate, direction
//   - Slot start–end in plant-local time
//   - Reference number (large, monospaced) — this is what the QR encodes
//   - QR code (client-side, qrcode.react → SVG; scannable, print-safe)
//   - Driver + vehicle summary
//
// Contrast: we deliberately paint the ticket card with a WHITE background and
// dark text so it is readable in both light and dark app themes and prints
// cleanly. This matches the fix mandated for the known "popup-in-light-mode"
// contrast bug: no CSS-variable text colour on the ticket surface.

import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Ticket, Truck, CalendarClock, Building2, DoorOpen, Hash } from "lucide-react";

const BLUE = "#185FA5";
const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "#0a0c10",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, color) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  color: color || "#0a0c10",
});

function Row({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-2 py-1" style={{ borderBottom: "1px dashed rgba(0,0,0,0.10)" }}>
      <span style={mono(10, "#4b5563", 700, "0.4px")}>{label}</span>
      <span className="flex-1" />
      <span data-testid={testId} style={mono(12, "#0a0c10", 700)}>{value || "—"}</span>
    </div>
  );
}

/**
 * <SlotTicket booking={obj} plantName={optional} />
 *
 * `booking` shape: whatever comes back from POST /bookings, GET /my-bookings,
 * or GET /gate/lookup. All must expose `reference`, `slot_start_local`,
 * `slot_end_local`, and `field_values`.
 */
export default function SlotTicket({ booking, plantName }) {
  if (!booking) return null;
  const fv = booking.field_values || {};
  const ref = booking.reference || "";
  const dir = booking.direction === "inbound" ? "Bejövő" : booking.direction === "outbound" ? "Kimenő" : "—";

  return (
    <div
      data-testid="ds-slot-ticket"
      className="mx-auto"
      style={{
        maxWidth: 520,
        background: "#ffffff",
        color: "#0a0c10",
        borderRadius: 10,
        border: `2px solid ${BLUE}`,
        boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}
    >
      {/* Header strip */}
      <div style={{ background: BLUE, color: "#fff", padding: "10px 14px" }} className="flex items-center gap-2">
        <Ticket size={16} />
        <span style={{ ...syne(15, 800, "#fff"), letterSpacing: "-0.4px" }}>Időkapu · Slot-jegy</span>
        <span className="flex-1" />
        <span style={mono(10, "#e0edfa", 700, "0.6px")}>SITROOM</span>
      </div>

      {/* Body */}
      <div className="p-4">
        {/* Reference — the QR payload */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <div style={mono(9, "#6b7280", 700, "0.6px")}>REFERENCIA-SZÁM</div>
            <div
              data-testid="ds-slot-ticket-ref"
              style={{
                ...mono(20, "#0a0c10", 800, "1.5px"),
                marginTop: 4,
              }}
            >
              {ref || "—"}
            </div>
          </div>
          <div
            data-testid="ds-slot-ticket-qr"
            style={{ background: "#fff", padding: 6, borderRadius: 4, border: "1px solid rgba(0,0,0,0.10)" }}
          >
            {ref ? (
              <QRCodeSVG value={ref} size={104} level="M" bgColor="#ffffff" fgColor="#0a0c10" />
            ) : (
              <div style={{ width: 104, height: 104, background: "#f3f4f6" }} />
            )}
          </div>
        </div>

        {/* Slot block */}
        <div
          className="rounded p-3 mb-3"
          style={{ background: "rgba(24,95,165,0.06)", border: `1px solid ${BLUE}` }}
        >
          <div className="flex items-baseline gap-2">
            <CalendarClock size={14} color={BLUE} />
            <span style={syne(12, 700, BLUE)}>IDŐSÁV</span>
          </div>
          <div className="mt-1" style={mono(18, BLUE, 800, "0.4px")}>
            <span data-testid="ds-slot-ticket-time">
              {booking.slot_start_local}–{booking.slot_end_local}
            </span>
          </div>
          <div style={mono(11, "#4b5563")}>
            {(booking.slot_start_local_iso || booking.slot_start || "").slice(0, 10)}
          </div>
        </div>

        {/* Meta grid */}
        <Row label="GYÁR" value={plantName || booking.plant_name} testId="ds-slot-ticket-plant" />
        <Row label="DOKK" value={booking.dock_name} testId="ds-slot-ticket-dock" />
        {booking.gate_code && <Row label="KAPU" value={booking.gate_code} testId="ds-slot-ticket-gate" />}
        <Row label="IRÁNY" value={dir} testId="ds-slot-ticket-dir" />
        <Row label="RENDSZÁM" value={fv.rendszam} testId="ds-slot-ticket-plate" />
        {fv.potkocsi_rendszam && <Row label="PÓTKOCSI" value={fv.potkocsi_rendszam} testId="ds-slot-ticket-trailer" />}
        <Row label="SZOLGÁLTATÓ" value={fv.szolgaltato_neve} testId="ds-slot-ticket-carrier" />
        {fv.sofor_neve && <Row label="SOFŐR" value={fv.sofor_neve} testId="ds-slot-ticket-driver" />}
        {fv.sofor_telefon && <Row label="SOFŐR TEL." value={fv.sofor_telefon} testId="ds-slot-ticket-driver-phone" />}
        {fv.vonal && <Row label="VONAL" value={fv.vonal} testId="ds-slot-ticket-line" />}
        {fv.to_szam && <Row label="TO SZÁM" value={fv.to_szam} testId="ds-slot-ticket-to" />}

        {/* Footer hint */}
        <div className="mt-3 rounded p-2" style={{ background: "#fefce8", border: "1px solid #eab308", ...mono(10, "#78350f") }}>
          Mutasd be ezt a jegyet a portánál — a kapuszolgálat a QR kódot vagy a referencia számot fogja beolvasni.
        </div>
      </div>
    </div>
  );
}

export function PrintButton({ targetTestId = "ds-slot-ticket" }) {
  return (
    <button
      type="button"
      data-testid="ds-slot-ticket-print"
      onClick={() => window.print()}
      className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
      style={{
        background: "transparent",
        border: `1px solid ${BLUE}`,
        color: BLUE,
        cursor: "pointer",
        ...mono(11, BLUE, 700, "0.4px"),
      }}
    >
      <Printer size={13} /> Nyomtatás
    </button>
  );
}
